// Shared logic for the polymorphic addresses feature (maker pickup, organisation
// delivery/billing, customer delivery). Kept framework-free so it can move to
// shared/ verbatim once site/app need the same matching/formatting.

const normalise = (value: any) => (value ?? "").toString().trim().toLowerCase()

export interface AddressScope {
	entityType: string
	entityId: string | number
	type: string
}

export const scopeKey = ({ entityType, entityId, type }: AddressScope) =>
	`${entityType}:${entityId}:${type}`

export const formatAddress = (address: any) => {
	if (!address) return ""
	return [address.line_1, address.line_2, address.city, address.zip].filter(Boolean).join(", ")
}

// The owning record (e.g. an order) stores a JSON *snapshot* of the address, not
// a foreign key, so which saved address it points at has to be a value match.
export const matchSnapshot = (list: any[], snapshot: any) => {
	if (!snapshot || !Array.isArray(list)) return null
	return (
		list.find(
			(address) =>
				normalise(address.line_1) === normalise(snapshot.line_1) &&
				normalise(address.line_2) === normalise(snapshot.line_2) &&
				normalise(address.city) === normalise(snapshot.city) &&
				normalise(address.zip) === normalise(snapshot.zip)
		) || null
	)
}

// LocationBox's `addressChanged` event carries `{ formatted, raw }` — `raw` is
// the Loqate Retrieve response item, which includes Latitude/Longitude.
export const fromLoqate = (raw: any) => ({
	line_1: raw?.Line1 || "",
	line_2: raw?.Line2 || "",
	city: raw?.City || "",
	zip: raw?.PostalCode || "",
	latitude: raw?.Latitude ?? null,
	longitude: raw?.Longitude ?? null,
})

export const isValidated = (address: any) => address?.latitude != null && address?.longitude != null

export const addressLabel = (address: any, entityName = "") => {
	if (!address) return ""
	const prefix = entityName ? `${entityName} — ` : ""
	const name = address.label || address.line_1
	const suffix = address.is_primary ? " (default)" : ""
	return `${prefix}${name}${suffix}`
}

export const archivedLabel = (address: any) =>
	`Archived address — ${address?.label || formatAddress(address)}`
